import React, { useState, useEffect } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorWhatThisMeans from './calc_components/CalculatorWhatThisMeans';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import './calculators.css';
import './EmiInputBands.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 6;
const maxInterestRate = 50;
const defaultInterestRate = 9;
const interestRateStep = 0.25;

const minTenureYears = 1;
const maxTenureYears = 30;
const defaultTenureYears = 20;

const minLoanAmount = 0;
const maxLoanAmount = 1000000000;
const defaultLoanAmount = 1000000;

const STANDALONE_DEFAULTS = {
  interestRate: defaultInterestRate,
  tenureYears: defaultTenureYears,
  loanAmount: defaultLoanAmount,
};

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return defaultInterestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(maxInterestRate, Math.max(minInterestRate, Number(snapped.toFixed(2))));
}

export function computeEmi(loanAmount, annualInterestRatePct, tenureYears) {
  const P = Number(loanAmount);
  const years = Number(tenureYears);
  if (!Number.isFinite(P) || P <= 0 || !Number.isFinite(years) || years <= 0) {
    return 0;
  }

  const n = Math.round(years * 12);
  const annualRate = Number(annualInterestRatePct);
  if (!Number.isFinite(annualRate) || annualRate <= 0) {
    return P / n;
  }

  const r = annualRate / 12 / 100;
  const factor = (1 + r) ** n;
  if (factor === 1) return P / n;
  return (P * r * factor) / (factor - 1);
}

export default function EmiCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const [interestRate, setInterestRate] = useState(STANDALONE_DEFAULTS.interestRate);
  const [tenureYears, setTenureYears] = useState(STANDALONE_DEFAULTS.tenureYears);
  const [loanAmount, setLoanAmount] = useState(STANDALONE_DEFAULTS.loanAmount);
  const [loanAmountForWords, setLoanAmountForWords] = useState(STANDALONE_DEFAULTS.loanAmount);

  const emi = computeEmi(loanAmount, interestRate, tenureYears);
  const loanAmountWords =
    loanAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(loanAmountForWords))
      : '';

  function handleStartOver(e) {
    e.preventDefault();
    setInterestRate(STANDALONE_DEFAULTS.interestRate);
    setTenureYears(STANDALONE_DEFAULTS.tenureYears);
    setLoanAmount(STANDALONE_DEFAULTS.loanAmount);
    setLoanAmountForWords(STANDALONE_DEFAULTS.loanAmount);
  }

  const calculatorCard = (
    <div className={`appContainer nps-ups--compact emi-calculator${embed ? ' nps-ups--embed' : ''}`}>
      <h1 className="tool-heading">EMI Calculator</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          Estimate your equated monthly instalment from loan amount, interest rate, and tenure.
        </p>
      </div>

      <EmiResultPanel emi={emi} />

      <div className="border-top-light nps-ups-divider" />

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="emi-interest-rate"
          label="Interest rate"
          tooltip="Annual interest rate on the loan. Illustrative only—actual rates vary by lender and product."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.interestRate}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="emi-tenure-years"
          label="Tenure in years"
          tooltip="Total repayment period for the loan, in whole years."
          value={tenureYears}
          setValue={setTenureYears}
          min={minTenureYears}
          max={maxTenureYears}
          step={1}
          format={formatYearsCount}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.tenureYears}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="emi-loan-amount"
            label="Loan amount"
            tooltip="Principal borrowed, before interest and fees."
            value={Math.min(loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(v, maxLoanAmount);
              setLoanAmount(next);
              setLoanAmountForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setLoanAmountForWords(Math.min(maxLoanAmount, Math.max(0, n)));
              } else {
                setLoanAmountForWords(0);
              }
            }}
            min={minLoanAmount}
            max={maxLoanAmount}
            step={100000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.loanAmount}
          />
          {loanAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {loanAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative EMI from the standard amortisation formula; excludes processing fees,
        insurance, prepayment, and tax effects. Not lender or product advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="emi-calculator" />
      </div>
    </div>
  );
}

function EmiResultPanel({ emi }) {
  const [showResult, setShowResult] = useState(true);
  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();
  const emiDisplay = formatInr(emi);
  const wordsLine =
    Number.isFinite(emi) && emi > 0 ? inrAmountInWordsShort(resolveAmount(emi)) : '';

  return (
    <div className="grid-center-display-result nps-ups-result-panel nps-ups-result-outer calc-result-legacy nps-result-legacy">
      <CalculatorPrecisionToggle />
      <button
        type="button"
        className="btn-small-round nps-ups-result-help"
        onClick={() => setShowResult((s) => !s)}
        aria-label={showResult ? 'Show explanation' : 'Show number'}
      >
        {showResult ? '?' : '×'}
      </button>

      {showResult ? (
        <>
          <h4>Monthly EMI amount</h4>
          <h1 className="nps-ups-years-figure engager-money-figure">{emiDisplay}</h1>
          {wordsLine ? (
            <p className="calc-output-amount-in-words">{wordsLine}</p>
          ) : null}
        </>
      ) : (
        <>
          <h4 className="calc-wtm-heading">What this means</h4>
          <CalculatorWhatThisMeans
            regionLabel="Explanation of your EMI result"
            heroValue={emiDisplay}
            heroVariant="inr"
            tagline={
              <p>
                <strong>Fixed monthly payment</strong> for this loan amount, rate, and tenure
                using the standard EMI formula.
              </p>
            }
            highlight={
              <>
                Assumes <em>fixed rate</em> and steady payments—actual schedules may differ.
              </>
            }
            highlightTone="warning"
            foot="Illustrative only—not advice."
          />
        </>
      )}
    </div>
  );
}
